<script>
import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { Size } from "@/abc-dsk/options";
import lang from "@/directives/lang";

import ArticleContentExample from "./ArticleContentExample.vue";
import ArticleContentTranslationEntry from "./ArticleContentTranslationEntry.vue";

export default {
  name: "ArticleContentTranslation",
  components: {
    ArticleContentExample,
    ArticleContentTranslationEntry,
    TgModal
  },
  directives: {
    lang
  },
  props: {
    translation: {
      type: Object,
      required: true
    },
    direction: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      Size,
      isPosterVisible: false
    };
  },
  computed: {
    shouldShowMeaningNumber() {
      const [majorMeaning = 0, minorMeaning = 0] = this.translation.meaningNumber
        .split(".")
        .filter((val) => val !== "")
        .map((val) => parseInt(val, 10));

      return majorMeaning > 0 && majorMeaning < 50 && minorMeaning === 0;
    },
    posterUrl() {
      return "https://static.ordbogen.com/politiken/feo/plancher/" + this.translation.poster;
    }
  },
  methods: {
    getTranslationExplanation(pos) {
      /**
       * 'Danish -> English' always shows the explanation after first translation,
       * but squeezed in before any inflections, so `ArticleContentTranslationEntry`
       * must render the explanation to be able to position it correctly.
       */
      if (this.direction === "daen") {
        const isFirstTranslation = pos === 1;
        return isFirstTranslation ? this.translation.explanation : "";
      }

      /**
       * 'English -> Danish' always shows explanation after all translations.
       */
      const isLastTranslation = !this.translation[`syn${pos + 1}`];
      return isLastTranslation ? this.translation.explanation : "";
    },
    showPoster() {
      this.isPosterVisible = true;
    },
    hidePoster() {
      this.isPosterVisible = false;
    }
  }
};
</script>

<template>
  <div>
    <span
      v-if="shouldShowMeaningNumber"
      class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
    >
      {{ translation.meaningNumber }}.
    </span>
    <template v-if="translation.idiom">
      <span
        v-lang:source
        class="article-content__text article-content__text--secondary"
        v-html="translation.idiom"
      ></span>
      <span class="article-content__text"> = </span>
    </template>

    <span class="article-content__translation-entries">
      <ArticleContentTranslationEntry
        v-if="translation.syn1"
        :word="translation.syn1"
        :inflections="translation.syn1Flexion"
        :pronunciation1="translation.pronunciationId"
        :pronunciation2="translation.syn1PronunciationId"
        :explanation="getTranslationExplanation(1)"
        :direction="direction"
      />
      <ArticleContentTranslationEntry
        v-if="translation.syn2"
        :word="translation.syn2"
        :comment="translation.syn2Note"
        :pronunciation2="translation.syn2PronunciationId"
        :explanation="getTranslationExplanation(2)"
        :direction="direction"
      />
      <ArticleContentTranslationEntry
        v-if="translation.syn3"
        :word="translation.syn3"
        :comment="translation.syn3Note"
        :pronunciation2="translation.syn3PronunciationId"
        :explanation="getTranslationExplanation(3)"
        :direction="direction"
      />
      <ArticleContentTranslationEntry
        v-if="translation.syn4"
        :word="translation.syn4"
        :comment="translation.syn4Note"
        :pronunciation2="translation.syn4PronunciationId"
        :explanation="getTranslationExplanation(4)"
        :direction="direction"
      />
      <ArticleContentTranslationEntry
        v-if="translation.syn5"
        :word="translation.syn5"
        :comment="translation.syn5Note"
        :pronunciation2="translation.syn5PronunciationId"
        :explanation="getTranslationExplanation(5)"
        :direction="direction"
      />
    </span>
    <a
      v-if="translation.poster"
      href="#"
      class="article-content__link article-content__link--small ml-1"
      @click="showPoster()"
      >{{ $t("article.poster.link") }}</a
    >
    <TgModal
      v-if="translation.poster"
      :size="Size.Xl"
      dialog-class="poster-modal"
      body-class="poster-modal__body"
      :is-open="isPosterVisible"
      @close="hidePoster()"
    >
      <template #modal-header>
        {{ $t("article.poster.title", { word: translation.syn1 }) }}
      </template>

      <img :src="posterUrl" :alt="$t('article.poster.title', { word: translation.syn1 })" />
    </TgModal>
    <ArticleContentExample
      v-if="translation.eks1"
      :l1-eks="translation.eks1"
      :l2-eks="translation.deks1"
    />
    <ArticleContentExample
      v-if="translation.eks2"
      :l1-eks="translation.eks2"
      :l2-eks="translation.deks2"
    />
    <ArticleContentExample
      v-if="translation.eks3"
      :l1-eks="translation.eks3"
      :l2-eks="translation.deks3"
    />
    <ArticleContentExample
      v-if="translation.eks4"
      :l1-eks="translation.eks4"
      :l2-eks="translation.deks4"
    />
  </div>
</template>
